/* eslint-disable prefer-promise-reject-errors */
import _ from 'underscore';
import { useCallback, useContext } from 'react';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useInstrumentLogger } from '@bingads-webui-campaign-react/linkedin-instrumentation';
import { getWizardData, useWizardService, WIZARD_EVENTS } from '@bingads-webui-fluent-component/wizard';
import { Campaign } from '@bingads-webui/campaign-odata-schemas';
import {
  ERROR_ENTITY,
  createOrUpdateCampaign,
  readCampaign,
  readCampaignCriterions,
  getDefaultLocation,
  genericApiError,
  getBulkUploadErrorType,
  mapBulkUpsertErrors,
  filterCriterionsByType,
  LocationCriterionTypes,
  AudienceCriterionTypes,
  AgeCriterionTypes,
  GenderCriterionTypes,
} from '@bingads-webui-campaign-react/linkedin-data';
import { useFetchGoalsFactory } from '@bingads-webui-campaign-react/goal-selector-v2';
import { useP$DaterangeDefault } from '@bingads-webui-campaign-react/wizard-shared-components';

import { useTargetingLocationsAdapter } from './use-targeting-locations-adapter';
import { useTargetingAudiencesAdapter } from './use-targeting-audiences-adapter';
import { useTargetingAgeAdapter } from './use-targeting-age-adapter';
import { useTargetingGenderAdapter } from './use-targeting-gender-adapter';
import { useCampaignConversionGoalsAdapter } from './use-campaign-conversion-goals-adapter';

const bulkUpsertEntityErrorMap = {
  [getBulkUploadErrorType(Campaign)]: ERROR_ENTITY.Campaign,
  'Campaign Location Criterion Error': ERROR_ENTITY.LocationTargeting,
  'Campaign Company Name Criterion Error': ERROR_ENTITY.AudienceTargeting,
  'Campaign Industry Criterion Error': ERROR_ENTITY.AudienceTargeting,
  'Campaign Job Function Criterion Error': ERROR_ENTITY.AudienceTargeting,
  'Campaign Job Title Criterion Error': ERROR_ENTITY.AudienceTargeting,
  'Campaign Job Seniority Criterion Error': ERROR_ENTITY.AudienceTargeting,
};

export const useTargetingStepRepository = () => {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);

  const logger = useInstrumentLogger({
    scenarioName: 'LinkedInTargetingStep',
    activityName: 'Repository',
  });

  const { scope } = useContext(ScopeContext);
  const { cid, aid } = scope.values;
  const { send, state } = useWizardService();
  const { campaignId, ads } = getWizardData(state);
  const { CurrentUser: { LocaleID: lcid } } = serverContext || {};
  const $dateRange = useP$DaterangeDefault();
  const fetchLICompatibleGoals = useFetchGoalsFactory({
    $dateRange,
    cid,
    aid,
    showTrackingStatusIcon: false,
    isLinkedInCompatibleGoals: true,
    i18n,
  });

  const {
    mapToViewModel: mapLocationsToViewModel,
    mapToBulkModel: mapLocationsToBulkModel,
  } = useTargetingLocationsAdapter({ campaignId });
  const {
    mapToViewModel: mapAudiencesToViewModel,
    mapToBulkModel: mapAudiencesToBulkModel,
  } = useTargetingAudiencesAdapter({ campaignId });
  const {
    mapToViewModel: mapAgeRangesToViewModel,
    mapToBulkModel: mapAgeRangesToBulkModel,
  } = useTargetingAgeAdapter({ campaignId });
  const {
    mapToViewModel: mapGendersToViewModel,
    mapToBulkModel: mapGendersToBulkModel,
  } = useTargetingGenderAdapter({ campaignId });
  const {
    mapToViewModel: mapCampaignConversionGoalToViewModel,
    mapToBulkModel: mapCampaignConversionGoalToBulkModel,
  } = useCampaignConversionGoalsAdapter({ campaignId });

  const load = useCallback(() => {
    const isMultiAdModel = ads && _.keys(ads).length > 1;
    send(WIZARD_EVENTS.UPDATE_FlOW_FACTORS, {
      flowFactors: {
        isMultiAdModel,
        isAdStepActive: false,
      },
    });

    const loadCampaign = () => readCampaign(cid, aid, campaignId);
    const loadCampaignCriterions = () => readCampaignCriterions(cid, aid, campaignId);
    const loadDefaultLocation = () => getDefaultLocation(cid, aid, lcid);
    const checkForCompatibleGoals = () => fetchLICompatibleGoals('', 1, 1);

    const initialRequests = [
      loadCampaign(),
      loadCampaignCriterions(),
      loadDefaultLocation(),
      checkForCompatibleGoals(),
    ];

    return Promise.all(initialRequests).then(([
      campaign,
      criterionsResponse,
      defaultLocation,
      checkCompatibleGoalResponse,
    ]) => {
      const criterions = _.get(criterionsResponse, 'value', []);

      const locationCriterions = filterCriterionsByType(criterions, LocationCriterionTypes);
      const audienceCriterions = filterCriterionsByType(criterions, AudienceCriterionTypes);
      const ageCriterions = filterCriterionsByType(criterions, AgeCriterionTypes);
      const genderCriterions = filterCriterionsByType(criterions, GenderCriterionTypes);
      const hasLinkedInCompatibleGoals = checkCompatibleGoalResponse.totalCount > 0;

      const data = {
        language: campaign.Languages[0],
        locations: mapLocationsToViewModel(locationCriterions, defaultLocation),
        audiences: mapAudiencesToViewModel(audienceCriterions),
        ageRanges: mapAgeRangesToViewModel(ageCriterions),
        genders: mapGendersToViewModel(genderCriterions),
        defaultLocation,
        conversionGoals: mapCampaignConversionGoalToViewModel(campaign.CampaignConversionGoal.Goals),
        hasLinkedInCompatibleGoals,
      };

      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
        hasLinkedInCompatibleGoals,
      });

      return data;
    }).catch((err) => {
      logger.error(`Failed to load targeting data: ${JSON.stringify(err)}`);
      Promise.reject(err);
    });
  }, [aid, campaignId, cid, lcid, send, ads, mapAudiencesToViewModel, mapLocationsToViewModel, mapAgeRangesToViewModel, mapGendersToViewModel, mapCampaignConversionGoalToViewModel, fetchLICompatibleGoals, logger]);

  const save = useCallback(({ data }) => {
    const campaign = {
      Id: campaignId,
      Languages: [data.language],
      CampaignCriterions: _.union(
        mapLocationsToBulkModel(data.locations),
        mapAudiencesToBulkModel(data.audiences),
        mapAgeRangesToBulkModel(data.ageRanges),
        mapGendersToBulkModel(data.genders)
      ),
      CampaignConversionGoalAssociations: mapCampaignConversionGoalToBulkModel(data.conversionGoals),
    };

    if (_.isEmpty(campaign.CampaignCriterions)) {
      delete campaign.CampaignCriterions;
    }

    return createOrUpdateCampaign(campaign)
      .then(() => {
        logger.trace('Campaign updated successfully');
        return data;
      })
      .catch((err) => {
        logger.error(`Failed to save campaign: ${JSON.stringify(err)}`);

        const errors = _.get(err, 'errors');
        const serverErrors = errors
          ? mapBulkUpsertErrors(errors, bulkUpsertEntityErrorMap, _.identity, null, i18n)
          : [genericApiError];

        return Promise.reject({ error: { serverErrors } });
      });
  }, [campaignId, i18n, mapAudiencesToBulkModel, mapLocationsToBulkModel, mapAgeRangesToBulkModel, mapGendersToBulkModel, mapCampaignConversionGoalToBulkModel, logger]);

  return {
    load,
    save,
  };
};
